import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { UserPlus, ListChecks, CreditCard, LineChart, ChevronRight } from 'lucide-react';
import { NavLink } from 'react-router-dom';
import { PRODUCT_NAME } from '../../lib/tenant';
import { LandingSectionShell } from './LandingSectionShell';

const steps = [
  {
    step: '01',
    title: 'Register',
    body: 'Create your organization workspace in under a minute. One admin, one secure tenant.',
    icon: UserPlus,
    href: '/register',
    cta: 'Create workspace',
    external: true,
  },
  {
    step: '02',
    title: 'Choose plan',
    body: 'Pick the tier that matches your volume — upgrade or downgrade as you scale.',
    icon: ListChecks,
    href: '#pricing',
    cta: 'View plans',
    external: false,
  },
  {
    step: '03',
    title: 'Complete payment',
    body: 'Pay securely with Razorpay auto-pay. Your workspace activates immediately after checkout.',
    icon: CreditCard,
    href: '/checkout',
    cta: 'Open checkout',
    external: true,
  },
  {
    step: '04',
    title: 'Sign in to dashboard',
    body: `Run collections, hand loans, and reporting inside ${PRODUCT_NAME} — data stays isolated per org.`,
    icon: LineChart,
    href: '/login',
    cta: 'Sign in',
    external: true,
  },
];

export default function SaasProductFlow() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });

  return (
    <LandingSectionShell id="flow">
      <div ref={ref} className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          className="mx-auto mb-14 max-w-3xl text-center"
        >
          <span className="inline-flex items-center gap-2 rounded-full border border-emerald-500/25 bg-emerald-500/10 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-emerald-300">
            How it works
          </span>
          <h2 className="mt-4 font-display text-3xl font-semibold tracking-tight text-white sm:text-4xl">
            Live in four steps
          </h2>
          <p className="mt-4 text-base leading-relaxed text-slate-400 sm:text-lg">
            Register your shop, pick a plan, pay with Razorpay, and sign in — branch ledgers, finance desk, and reports
            ready on day one.
          </p>
        </motion.div>

        <div className="relative mx-auto grid max-w-6xl gap-4 md:grid-cols-2 lg:grid-cols-4 lg:gap-5">
          <div
            className="pointer-events-none absolute left-0 right-0 top-[50%] hidden h-px bg-gradient-to-r from-transparent via-emerald-500/30 to-transparent lg:block"
            aria-hidden
          />

          {steps.map((s, i) => (
            <motion.div
              key={s.step}
              initial={{ opacity: 0, y: 32, rotateX: 12 }}
              animate={inView ? { opacity: 1, y: 0, rotateX: 0 } : {}}
              transition={{ duration: 0.55, delay: 0.1 * i, ease: [0.22, 1, 0.36, 1] }}
              whileHover={{ y: -6, scale: 1.02 }}
              className="landing-glow-ring rounded-2xl p-[1px]"
              style={{ transformStyle: 'preserve-3d', perspective: 1000 }}
            >
              <div className="group flex min-h-[260px] flex-col rounded-2xl border border-white/10 bg-zinc-950/70 p-6 backdrop-blur-xl lg:min-h-[280px]">
                <div className="mb-5 flex items-start justify-between gap-3">
                  <span className="font-mono text-[11px] font-bold tracking-widest text-emerald-400">{s.step}</span>
                  <motion.div
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-gradient-to-br from-emerald-500/20 to-cyan-500/10 text-emerald-300"
                    whileHover={{ rotate: 8, scale: 1.08 }}
                  >
                    <s.icon className="h-5 w-5" />
                  </motion.div>
                </div>
                <h3 className="font-display text-lg font-semibold text-white">{s.title}</h3>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-400">{s.body}</p>
                <div className="mt-6 border-t border-white/[0.06] pt-4">
                  {s.external ? (
                    <NavLink
                      to={s.href}
                      className="inline-flex items-center gap-1 text-sm font-semibold text-emerald-400 transition-all hover:gap-2 hover:text-emerald-300"
                    >
                      {s.cta}
                      <ChevronRight className="h-4 w-4" />
                    </NavLink>
                  ) : (
                    <a
                      href={s.href}
                      className="inline-flex items-center gap-1 text-sm font-semibold text-emerald-400 transition-all hover:gap-2 hover:text-emerald-300"
                    >
                      {s.cta}
                      <ChevronRight className="h-4 w-4" />
                    </a>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </LandingSectionShell>
  );
}
